import { Mic, MicOff, Video, VideoOff } from "lucide-react";
import { toast } from "sonner";
import { ulid } from "ulid";
import { useStore } from "zustand";
import { ProfileOrganization } from "@/components/app/profile-organization";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuLabel,
	ContextMenuSeparator,
	ContextMenuSub,
	ContextMenuSubContent,
	ContextMenuSubTrigger,
	ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
	derivePermissionLevel,
	useSelfPermissionsOnChannel,
} from "@/hooks/use-accessible-channels";
import { useChannelParticipants } from "@/hooks/use-presence";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { ws } from "@/lib/websocket-wrapper";

const PARTICIPANT_NAME_MAX_CHARS = 15;

type ChannelParticipantsProps = {
	channelId: string;
};

export function ChannelParticipants({ channelId }: ChannelParticipantsProps) {
	const participants = useChannelParticipants(channelId);
	const permissionLevel = useSelfPermissionsOnChannel(channelId);
	const isModerator = permissionLevel === "moderate";

	const user = useStore(authStore, (state) => state.user);
	const channelMap = useStore(coreStore, (state) => state.channels);
	const categoriesMap = useStore(coreStore, (state) => state.categories);

	if (participants.length === 0) {
		return null;
	}

	const getInitials = (user: (typeof participants)[0]["user"]) => {
		if (!user) return "?";
		const name = user.profile.displayName;
		if (name) return name.charAt(0).toUpperCase();
		return "?";
	};

	const getUserDisplayName = (user: (typeof participants)[0]["user"]) => {
		if (!user) return "Unknown User";
		return user.profile.displayName || "Unknown User";
	};

	let moveTargetGroups: {
		categoryId: string | null;
		categoryName: string;
		channels: { id: string; name: string }[];
	}[] = [];
	if (isModerator && user?.uid) {
		const filteredChannels = Array.from(channelMap.values()).filter(
			(ch) =>
				(ch.type === "voice_and_text" || ch.type === "stage") &&
				ch.id !== channelId &&
				derivePermissionLevel(ch) === "moderate"
		);

		const grouped = new Map<
			string | null,
			{ id: string; name: string; order: number }[]
		>();
		for (const ch of filteredChannels) {
			const catId = ch.categoryId ?? null;
			if (!grouped.has(catId)) {
				grouped.set(catId, []);
			}
			grouped.get(catId)!.push({
				id: ch.id,
				name: ch.name,
				order: ch.order ?? 0,
			});
		}

		for (const channels of grouped.values()) {
			channels.sort((a, b) => a.order - b.order);
		}

		// Uncategorized sorts last.
		moveTargetGroups = Array.from(grouped.entries())
			.sort(([a], [b]) => {
				if (a === null) return 1;
				if (b === null) return -1;
				const catA = categoriesMap.get(a);
				const catB = categoriesMap.get(b);
				return (catA?.order ?? 0) - (catB?.order ?? 0);
			})
			.map(([catId, channels]) => ({
				categoryId: catId,
				categoryName: catId
					? (categoriesMap.get(catId)?.name ?? "Unknown")
					: "Uncategorized",
				channels,
			}));
	}
	const moveTargetChannels = moveTargetGroups.flatMap((g) => g.channels);

	const handleMoveUser = (
		targetFirebaseUid: string,
		targetChannelId: string
	) => {
		ws.sendWithPromise({
			id: ulid(),
			procedure: "livekit.moveUser",
			data: {
				targetFirebaseUid,
				sourceChannelId: channelId,
				targetChannelId,
			},
		}).catch((error) => {
			console.error("Failed to move user:", error);
			toast.error("Failed to move user. You may not have permission.");
		});
	};

	const showContextMenu = isModerator && moveTargetChannels.length > 0;

	return (
		<div className="flex flex-col gap-0.5 py-0.5 pr-1 pl-5.5">
			{participants.map((participant) => {
				const participantRow = (
					<div className="flex items-center gap-2 rounded px-2 py-0.5 hover:bg-accent/50">
						<Avatar className="size-5 border border-border/50">
							{participant.user?.profile.profilePhoto ? (
								<AvatarImage
									alt={getUserDisplayName(participant.user)}
									src={participant.user.profile.profilePhoto}
								/>
							) : null}
							<AvatarFallback className="bg-primary/10 font-medium text-[10px] text-primary">
								{getInitials(participant.user)}
							</AvatarFallback>
						</Avatar>
						<span className="flex-1 truncate text-muted-foreground text-xs">
							{getUserDisplayName(participant.user).length >
							PARTICIPANT_NAME_MAX_CHARS
								? `${getUserDisplayName(participant.user).slice(0, PARTICIPANT_NAME_MAX_CHARS)}...`
								: getUserDisplayName(participant.user)}
						</span>
						<ProfileOrganization
							className="min-w-0"
							uid={participant.firebaseUid}
						/>
						<div className="flex items-center gap-1">
							{participant.isMicrophoneEnabled ? (
								<Mic className="size-3 text-green-600 dark:text-green-500" />
							) : (
								<MicOff className="size-3 text-muted-foreground/50" />
							)}
							{participant.isCameraEnabled ? (
								<Video className="size-3 text-green-600 dark:text-green-500" />
							) : (
								<VideoOff className="size-3 text-muted-foreground/50" />
							)}
						</div>
					</div>
				);

				if (showContextMenu) {
					return (
						<ContextMenu key={participant.firebaseUid}>
							<ContextMenuTrigger asChild>{participantRow}</ContextMenuTrigger>
							<ContextMenuContent>
								<ContextMenuSub>
									<ContextMenuSubTrigger>Move to</ContextMenuSubTrigger>
									<ContextMenuSubContent className="max-h-80 overflow-y-auto">
										{moveTargetGroups.map((group, groupIdx) => (
											<div key={group.categoryId ?? "uncategorized"}>
												{groupIdx > 0 && <ContextMenuSeparator />}
												<ContextMenuLabel className="text-muted-foreground text-xs">
													{group.categoryName}
												</ContextMenuLabel>
												{group.channels.map((ch) => (
													<ContextMenuItem
														key={ch.id}
														onClick={() =>
															handleMoveUser(participant.firebaseUid, ch.id)
														}
													>
														{ch.name}
													</ContextMenuItem>
												))}
											</div>
										))}
									</ContextMenuSubContent>
								</ContextMenuSub>
							</ContextMenuContent>
						</ContextMenu>
					);
				}

				return <div key={participant.firebaseUid}>{participantRow}</div>;
			})}
		</div>
	);
}
